/**
 * Calendar and clock helpers for the grids. Dates stay `YYYY-MM-DD` strings and times `HH:MM` wall-clock strings in the
 * business zone (CLAUDE.md rule 3). `zonedToEpoch` is the one place a local date and time become an instant (a missed
 * punch needs one); it asks Intl for the zone's offset instead of doing any offset arithmetic of its own.
 */
import { addDays } from "../../lib/dates";

/** 0 = Sunday, as the PT availability and class slots use. Pure calendar arithmetic, no zone involved. */
export const weekdayOf = (ymd: string): number => new Date(`${ymd}T00:00:00Z`).getUTCDay();

/** Monday of the week containing `ymd`. */
export const weekStart = (ymd: string): string => addDays(ymd, -((weekdayOf(ymd) + 6) % 7));

export const weekDates = (ymd: string): string[] => Array.from({ length: 7 }, (_, i) => addDays(weekStart(ymd), i));

export const toMinutes = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
export const fromMinutes = (m: number): string => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

export interface Placed<T> { item: T; lane: number; lanes: number; start: number; end: number }

/** Side-by-side lanes for overlapping blocks: a cluster of overlaps shares its width, so 30 classes in a day stay readable. */
export function layoutLanes<T>(items: readonly T[], range: (t: T) => { start: string; end: string }): Placed<T>[] {
  const sorted = items.map((item) => ({ item, start: toMinutes(range(item).start), end: toMinutes(range(item).end) })).sort((a, b) => a.start - b.start || a.end - b.end);
  const out: Placed<T>[] = [];
  let cluster: Placed<T>[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = cluster.reduce((m, p) => Math.max(m, p.lane + 1), 0);
    for (const p of cluster) p.lanes = lanes;
    out.push(...cluster);
    cluster = [];
  };
  const laneEnds: number[] = [];
  for (const s of sorted) {
    if (cluster.length > 0 && s.start >= clusterEnd) { flush(); laneEnds.length = 0; }
    let lane = laneEnds.findIndex((e) => e <= s.start);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = s.end;
    cluster.push({ ...s, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, s.end);
  }
  flush();
  return out;
}

/** Snap a vertical offset in a column to `step` minutes after the grid's first minute. */
export function minutesFromOffset(offsetPx: number, pxPerMinute: number, gridStartMinute: number, step = 15): number {
  const raw = gridStartMinute + offsetPx / pxPerMinute;
  return Math.max(0, Math.round(raw / step) * step);
}

function offsetMinutes(epochMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(epochMs);
  const g = (t: string) => Number(parts.find((p) => p.type === t)!.value);
  return (Date.UTC(g("year"), g("month") - 1, g("day"), g("hour"), g("minute"), g("second")) - Math.floor(epochMs / 1000) * 1000) / 60_000;
}

/** The instant at which the wall clock of `timeZone` reads `date` `time`. Two passes settle the offset across a DST change. */
export function zonedToEpoch(date: string, time: string, timeZone: string): number {
  const [y, mo, d] = date.split("-").map(Number) as [number, number, number];
  const naive = Date.UTC(y, mo - 1, d, Number(time.slice(0, 2)), Number(time.slice(3, 5)));
  let guess = naive - offsetMinutes(naive, timeZone) * 60_000;
  guess = naive - offsetMinutes(guess, timeZone) * 60_000;
  return guess;
}
